import classNames from 'classnames'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { AiOutlineInfoCircle } from 'react-icons/ai'

import { DietDetails, FolderDiets } from '../../api/services/dietbase/types.ts'
import Accordion from '../../components/common/Accordion'
import Alert from '../../components/common/Alert/Alert.tsx'
import HoverHint from '../../components/common/HoverHint'
import Popup from '../../components/common/Popup/Popup.tsx'
import TailButton from '../../components/common/TailButton/TailButton.tsx'
import useDietBaseFolders from '../../hooks/useDietBaseFolders.ts'
import DietDetailsPopup from './popup'

const DietList = ({
  folderID,
  useDietsIds,
  isGlobal,
  isEditable = true,
  getDietDetails,
  getStatusLoadingDietList
}: {
  folderID: number
  useDietsIds?: number[]
  isGlobal: boolean
  isEditable?: boolean
  getDietDetails?: (details: DietDetails | null, dietID: number | null) => void
  getStatusLoadingDietList?: (status: boolean) => void
}) => {
  const { loading, fetchDiets, fetchDietDetails, removeDiet } =
    useDietBaseFolders()

  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [folderDiets, setFolderDiets] = useState<FolderDiets[]>([])
  const [selectedDiet, setSelectedDiet] = useState<number | null>(null)
  const [dietDetails, setDietDetails] = useState<
    DietDetails | null | undefined
  >(null)

  useLayoutEffect(() => {
    const fetchData = async () => {
      const { diets } = await fetchDiets(folderID)
      setFolderDiets(diets)
    }
    fetchData()
  }, [])

  useEffect(() => {
    getStatusLoadingDietList && getStatusLoadingDietList(loading)
  }, [loading])

  useEffect(() => {
    if (dietDetails === null) setSelectedDiet(null)
  }, [dietDetails])

  const displayUnitNumber = (
    value: string,
    unit: string,
    hint: string,
    color: string
  ) => {
    return (
      <HoverHint hintLabel={hint}>
        <div className={`text-white ${color} rounded-2xl px-2`}>
          <span className={'font-bold'}>
            {`${Number(value).toFixed(0)} ${unit}`}
          </span>
        </div>
      </HoverHint>
    )
  }

  const fetchDetails = async (dietID: number) => {
    setShowPopup(true)
    const data = await fetchDietDetails(dietID)
    if (data !== null && data !== undefined) {
      setDietDetails(data)
      getDietDetails && getDietDetails(data, dietID)
    }
  }

  const removeDietHandler = async (deleteDietId: number) => {
    const isRemoved = await removeDiet(deleteDietId as number)
    if (!isRemoved) return

    setFolderDiets((prevDiets) => {
      return prevDiets.filter((diet) => diet.id !== deleteDietId)
    })
  }

  return (
    <Accordion title={'Diety'}>
      {() => (
        <div
          className={
            'flex-1 flex md:justify-normal justify-center flex-wrap gap-8 md:gap-4 mt-4'
          }
        >
          {folderDiets && folderDiets.length === 0 && !loading && (
            <HoverHint
              hintLabel={
                'Dodawanie diet jest możliwe z poziomu programu Profidiet -> Moja baza -> Baza diet'
              }
              ignoreStyle={true}
            >
              <div>
                <Alert
                  type="info"
                  message="Brak zaimportowancyh diet"
                  icon={<AiOutlineInfoCircle className="text-lg" />}
                />
              </div>
            </HoverHint>
          )}

          {folderDiets &&
            folderDiets.length > 0 &&
            folderDiets.map((diet) => (
              <div
                key={diet.id}
                onClick={() => fetchDetails(diet.id)}
                className={'relative'}
              >
                {useDietsIds && useDietsIds.includes(diet.id) && (
                  <span className="absolute top-1 left-0 bg-red-500 text-[14px] text-white px-4 rounded-2xl z-999">
                    Dieta była już wysłana do aplikacji
                  </span>
                )}
                <TailButton
                  key={diet.id}
                  id={diet.id}
                  title={diet.name}
                  tailSelected={selectedDiet}
                  setTailSelected={setSelectedDiet}
                  className={classNames(
                    'w-60 bg-gray-50 dark:bg-gray-800 rounded pt-8',
                    { 'pt-9': !isGlobal },
                    { 'pt-4': isGlobal }
                  )}
                  onDelete={
                    !isGlobal && isEditable
                      ? () => removeDietHandler(diet.id)
                      : undefined
                  }
                >
                  <div
                    className={
                      'flex flex-row items-center justify-between gap-4 mb-2'
                    }
                  >
                    <div
                      className={
                        'flex flex-col items-end text-[11px] border-r-2 pr-2'
                      }
                    >
                      {diet.day_events_hours.map((event) => (
                        <span key={event} className={''}>
                          {event.slice(0, 1) !== '0' ? event.slice(0, 1) : ''}
                          {event.slice(1, -3)}
                        </span>
                      ))}
                    </div>

                    <div className={'flex flex-col items-center text-[12px]'}>
                      <div>
                        {displayUnitNumber(
                          diet.nutritional_info.energy,
                          ' kcal',
                          'Węglowodany',
                          'bg-blue-400'
                        )}
                      </div>
                      <div className={'flex gap-1 mt-4'}>
                        {displayUnitNumber(
                          diet.nutritional_info.carbohydrate,
                          ' g',
                          'Węglowodany',
                          'bg-red-500'
                        )}
                        {displayUnitNumber(
                          diet.nutritional_info.protein,
                          ' g',
                          'Białko',
                          'bg-green-600'
                        )}
                        {displayUnitNumber(
                          diet.nutritional_info.fat,
                          ' g',
                          'Tłuszcze',
                          'bg-yellow-500'
                        )}
                      </div>
                    </div>
                  </div>
                </TailButton>
              </div>
            ))}

          {isEditable && (
            <Popup
              isVisible={showPopup}
              onClose={() => {
                setShowPopup(false)
                setDietDetails(null)
              }}
              size={'auto'}
            >
              <DietDetailsPopup
                dietDetails={dietDetails}
                onClose={() => {
                  setShowPopup(false)
                  setDietDetails(null)
                }}
                loading={loading}
              />
            </Popup>
          )}
        </div>
      )}
    </Accordion>
  )
}

export default DietList
